import type { BlogApiClient, TokenProvider } from './client';
import { BlogApiError } from './errors';
import type {
  AdminSiteConfig,
  AdminSiteSettings,
  CommunityAccess,
  CourseDetail,
  CoursePurchase,
  CoursePurchaseInput,
  CourseResources,
  CourseSummary,
  Entitlement,
  HistoryItem,
  LessonContent,
  MemberProfile,
  MemberProfileInput,
  NotificationInput,
  NotificationsPage,
  SiteConfig,
  SiteLink,
  SiteLinksInput,
  SiteNotification,
  SiteSettingsInput,
  TeamInput,
  TeamMember,
  Track,
} from './types';

interface HttpClientOptions {
  /** Origin (ou origem+prefixo) da Members API — `VITE_PORTAL_API_URL`. Sem barra final. */
  baseUrl: string;
  getToken: TokenProvider;
}

/**
 * Client real do contrato da Members API. Anexa um Bearer fresco por requisição
 * nas rotas autenticadas e normaliza as falhas para {@link BlogApiError}.
 * Sem estado além do provedor de token injetado.
 */
export class HttpBlogApiClient implements BlogApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;

  constructor({ baseUrl, getToken }: HttpClientOptions) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.getToken = getToken;
  }

  // ---- Vitrine pública (sem Bearer: o visitante não baixa o SDK de auth) ----------

  getSite(): Promise<SiteConfig> {
    return this.request('GET', '/site', { anonymous: true });
  }

  listTracks(): Promise<Track[]> {
    return this.request('GET', '/tracks', { anonymous: true });
  }

  listCourses(): Promise<CourseSummary[]> {
    return this.request('GET', '/courses', { anonymous: true }).then(toCourseSummaries);
  }

  getCourse(slug: string): Promise<CourseDetail> {
    // Com Bearer quando há sessão: o curso fora do ar abre para o admin e para o dono.
    return this.request('GET', `/courses/${encodeURIComponent(slug)}`).then((raw) =>
      toCourseDetail(raw as CourseDetailWire),
    );
  }

  // ---- Inscrição e compra (autenticadas) --------------------------------------

  enrollFreeCourse(slug: string): Promise<Entitlement> {
    return this.request('POST', `/courses/${encodeURIComponent(slug)}/enrollment`);
  }

  startCoursePurchase(slug: string, input: CoursePurchaseInput): Promise<CoursePurchase> {
    return this.request('POST', `/courses/${encodeURIComponent(slug)}/purchases`, { body: input });
  }

  listMyEntitlements(): Promise<Entitlement[]> {
    return this.request('GET', '/me/entitlements');
  }

  listMyPurchases(): Promise<CoursePurchase[]> {
    return this.request('GET', '/me/purchases');
  }

  getPurchase(purchaseId: string): Promise<CoursePurchase> {
    return this.request('GET', `/me/purchases/${encodeURIComponent(purchaseId)}`);
  }

  cancelPurchase(purchaseId: string): Promise<CoursePurchase> {
    return this.request('POST', `/me/purchases/${encodeURIComponent(purchaseId)}/cancellation`);
  }

  // ---- Conteúdo ----------------------------------------------------------------

  getLessonContent(courseSlug: string, lessonId: string): Promise<LessonContent> {
    // O Bearer vai junto QUANDO HÁ sessão: a amostra é pública, mas a leitura
    // logada de uma amostra não pode virar 401 por ter um token.
    return this.request(
      'GET',
      `/courses/${encodeURIComponent(courseSlug)}/lessons/${encodeURIComponent(lessonId)}/content`,
    );
  }

  // ---- Área do membro ---------------------------------------------------------------

  getMyProfile(): Promise<MemberProfile> {
    return this.request('GET', '/me/profile');
  }

  updateMyProfile(input: MemberProfileInput): Promise<MemberProfile> {
    return this.request('POST', '/me/profile', { body: input });
  }

  listMyHistory(): Promise<HistoryItem[]> {
    return this.request('GET', '/me/history');
  }

  listMyResources(): Promise<CourseResources[]> {
    return this.request('GET', '/me/resources');
  }

  getMyCommunity(): Promise<CommunityAccess> {
    return this.request('GET', '/me/community');
  }

  listMyNotifications(): Promise<NotificationsPage> {
    return this.request('GET', '/me/notifications');
  }

  markNotificationsSeen(): Promise<NotificationsPage> {
    return this.request('POST', '/me/notifications/seen');
  }

  // ---- Administração -------------------------------------------------------------

  adminGetSite(): Promise<AdminSiteConfig> {
    return this.request('GET', '/admin/site');
  }

  adminSaveSiteSettings(input: SiteSettingsInput): Promise<AdminSiteSettings> {
    return this.request('POST', '/admin/site/settings', { body: input });
  }

  adminSaveSiteLinks(input: SiteLinksInput): Promise<SiteLink[]> {
    return this.request('POST', '/admin/site/links', { body: input });
  }

  adminSaveTeam(input: TeamInput): Promise<TeamMember[]> {
    return this.request('POST', '/admin/team', { body: input });
  }

  adminListNotifications(): Promise<SiteNotification[]> {
    return this.request('GET', '/admin/notifications');
  }

  adminSaveNotification(input: NotificationInput): Promise<SiteNotification> {
    return this.request('POST', '/admin/notifications', { body: input });
  }

  adminDeleteNotification(notificationId: string): Promise<{ deleted: true }> {
    return this.request('POST', `/admin/notifications/${encodeURIComponent(notificationId)}/delete`);
  }

  // ---- Plumbing ----------------------------------------------------------------

  private async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; anonymous?: boolean } = {},
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };

    const token = opts.anonymous ? null : await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      response = await fetch(this.baseUrl + path, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });
    } catch {
      // A causa (TypeError do fetch) não sobe: não ajuda o leitor e pode citar a URL.
      throw new BlogApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
      });
    }

    if (!response.ok) {
      throw await toBlogApiError(response);
    }

    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
}

/** O resumo como a API de rollback (Lambda) o manda: sem `available` nem `track`. */
export type CourseSummaryWire = Omit<CourseSummary, 'available' | 'track'> &
  Partial<Pick<CourseSummary, 'available' | 'track'>>;

/**
 * Normaliza um item do catálogo: a Lambda de rollback não conhece trilha nem
 * "em breve", então a ausência vale curso no ar e sem trilha.
 */
export function toCourseSummary(raw: CourseSummaryWire): CourseSummary {
  return { ...raw, available: raw.available ?? true, track: raw.track ?? null };
}

/** Uma resposta fora do formato vira catálogo vazio (a vitrine mostra o estado vazio). */
function toCourseSummaries(raw: unknown): CourseSummary[] {
  return Array.isArray(raw) ? (raw as CourseSummaryWire[]).map(toCourseSummary) : [];
}

/** O detalhe como a API de rollback o manda: o resumo antigo mais a descrição e as lições. */
export type CourseDetailWire = Omit<CourseDetail, 'available' | 'track'> &
  Partial<Pick<CourseDetail, 'available' | 'track'>>;

/**
 * A ficha do curso sem `available` (Lambda de rollback ou função antiga) é um
 * curso no ar: sem isto a tela mostraria "em breve" e esconderia a compra.
 */
export function toCourseDetail(raw: CourseDetailWire): CourseDetail {
  return { ...raw, available: raw.available ?? true, track: raw.track ?? null };
}

/**
 * Lê o envelope `{code, message}` da Members API. Sem ele — resposta sem JSON,
 * ou um JSON alheio como o `{"message":"Unauthorized"}` do API Gateway ou o HTML
 * de um proxy — o código é `http_<status>` e a frase é a pt-BR do status
 * ({@link BlogApiError}), nunca o texto cru da resposta.
 */
async function toBlogApiError(response: Response): Promise<BlogApiError> {
  const envelope = readEnvelope(await response.json().catch(() => null));
  return new BlogApiError(response.status, {
    code: envelope?.code ?? `http_${response.status}`,
    message: envelope?.message,
  });
}

function readEnvelope(body: unknown): { code: string; message?: string } | null {
  if (typeof body !== 'object' || body === null) return null;
  const { code, message } = body as { code?: unknown; message?: unknown };
  if (typeof code !== 'string') return null;
  return { code, message: typeof message === 'string' ? message : undefined };
}
